import first from "lodash/first";
import isEmpty from "lodash/isEmpty";
import last from "lodash/last";
import { getOriginalResource } from "PFApp/booking/parts/helpers";
import { BookingDetailSidePanelProfile } from "PFApp/booking/parts/overview/detail_view/booking_detail/booking_detail_side_panel_context/booking_detail_side_panel_context.types";
import { RowsWithRefs, RowWithRef } from "PFApp/booking/store";
import { CalendarMode, Row } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { ProfileBasic, RowOriginal, RowType, WorkforceItemResource } from "PFApp/booking/types/row";
import { Booking, Id } from "PFTypes";
import { RefObject } from "react";

import { Day } from "../../../../booking_module_calendar/booking_calendar/booking_calendar_store";
import { getDaysDifference, setTimeToMidnight } from "../../../calendar.utils";
import getUpdatedDates from "./get_updated_dates";
import { DnDMode } from "./index";
import { Position } from "./use_drag_and_drop";

export const getBookingDatesDiff = (booking: Booking, start: Date, end: Date): number =>
  getDaysDifference(
    setTimeToMidnight(new Date(start.getTime())),
    setTimeToMidnight(new Date(booking.start_date))
  ) ||
  getDaysDifference(
    setTimeToMidnight(new Date(end.getTime())),
    setTimeToMidnight(new Date(booking.end_date))
  );

// returns distance between row start and client mouse
export const getDistanceFromRowStartToClientX = (
  clientX: number,
  rowRef: HTMLDivElement | undefined
): number => {
  const rowX = rowRef?.getBoundingClientRect().x ?? 0;
  // if row spans outside the viewport (row x < 0) we need to add clientX + abs(row x)
  // if row doesn't span outside the viewport (row x > 0, large zoom), we need to subtract it from clientX
  return clientX + -1 * rowX;
};

const getDayIndex = (clientX: number, days: Day[]): number =>
  days.findIndex(({ left }) => left > clientX) - 1;

type GetNumberOfDraggedDays = {
  currentMouseX: number;
  originClickX: number;
  rowRef: HTMLDivElement | undefined;
  days: Day[];
};

type GetNumberOfDraggedDaysReturn = {
  draggedDaysNumber: number;
  draggedDayIndex: number;
};

export const getNumberOfDraggedDays = ({
  currentMouseX,
  days,
  originClickX,
  rowRef
}: GetNumberOfDraggedDays): GetNumberOfDraggedDaysReturn => {
  const clientClickX = getDistanceFromRowStartToClientX(currentMouseX, rowRef);
  const delta = clientClickX - originClickX;

  const { left: lastDayLeft, width: lastDayWidth } = last(days)!;

  const currentMouseLeft = Math.min(Math.max(originClickX + delta, 0), lastDayLeft + lastDayWidth);
  const originDayIndex = getDayIndex(originClickX, days);
  const draggedDayIndex = getDayIndex(currentMouseLeft, days);

  const isMovingLeft = delta < 0;
  const draggedDayIndexLimited = draggedDayIndex < 0 ? (isMovingLeft ? 0 : days.length - 1) : draggedDayIndex;

  return {
    draggedDaysNumber: draggedDayIndexLimited - originDayIndex,
    draggedDayIndex: draggedDayIndexLimited
  };
};

type ShouldMoveBooking = {
  clientPosition: Position;
  draggingOriginClick: React.RefObject<Position | null>;
  rowRef: HTMLDivElement | undefined;
  draggedDays: React.RefObject<number | null>;
  days: Day[];
  rowsWithRefs?: RowsWithRefs<Row>;
};

export const shouldMoveBookingHorizontally = ({
  clientPosition,
  draggingOriginClick,
  draggedDays,
  rowRef,
  days
}: ShouldMoveBooking) => {
  if (!draggingOriginClick.current) {
    return false;
  }

  const { draggedDaysNumber } = getNumberOfDraggedDays({
    currentMouseX: clientPosition.x,
    originClickX: draggingOriginClick.current.x,
    rowRef,
    days
  });

  return draggedDaysNumber !== draggedDays.current;
};

export const shouldMoveBookingXY = ({
  clientPosition,
  draggingOriginClick,
  draggedDays,
  rowRef,
  days
}: ShouldMoveBooking): boolean => {
  const shouldMoveHorizontally = shouldMoveBookingHorizontally({
    clientPosition,
    draggingOriginClick,
    draggedDays,
    rowRef,
    days
  });

  const shouldMoveVertically = clientPosition.y !== draggingOriginClick.current?.y;

  return shouldMoveHorizontally || shouldMoveVertically;
};

type PerformDragMoveCalculations = {
  clientX: number;
  draggingOriginClick: React.RefObject<Position | null>;
  rowRef: HTMLDivElement | undefined;
  days: Day[];
  bookingPastDaysLimit: number;
  draggingMode: React.RefObject<DnDMode | null>;
  draggedDays: React.RefObject<number | null>;
  booking: Booking;
};

type PerformDragMoveCalculationsReturn = {
  startDate: Date;
  endDate: Date;
  draggedDaysNumber: number;
};

export const calculateHorizontalDragMove = ({
  booking,
  clientX,
  days,
  draggedDays,
  draggingMode,
  draggingOriginClick: draggingOriginClickX,
  bookingPastDaysLimit,
  rowRef
}: PerformDragMoveCalculations): PerformDragMoveCalculationsReturn | null => {
  if (!draggingMode.current || !draggingOriginClickX.current) {
    return null;
  }
  const { draggedDaysNumber } = getNumberOfDraggedDays({
    currentMouseX: clientX,
    originClickX: draggingOriginClickX.current.x,
    rowRef,
    days
  });

  if (draggedDaysNumber === draggedDays.current) {
    return null;
  }

  const { startDate, endDate, daysDiffLimited } = getUpdatedDates(
    draggingMode.current,
    draggedDaysNumber,
    booking?.start_date,
    booking?.end_date,
    days,
    bookingPastDaysLimit
  );

  if (daysDiffLimited === draggedDays.current) {
    return null;
  }

  return {
    startDate,
    endDate,
    draggedDaysNumber
  };
};

export const getTargetRowWithRef = (
  rowsWithRefs: RowsWithRefs<Row>,
  clientY: number,
  rows: Row[],
  calendarMode: CalendarMode
): RowWithRef<Row> | null => {
  const rowsArray = Array.from(rowsWithRefs.values());
  const rowWithRef =
    rowsArray.find((rowWithRef) => {
      if (rowWithRef.rowRef) {
        const { top, bottom } = rowWithRef.rowRef.getBoundingClientRect();
        return clientY > top - 1 && clientY < bottom + 1;
      }
      return false;
    }) ?? null;

  if (calendarMode === CalendarMode.Projects) {
    return rowWithRef;
  }

  const targetParentRowKey = getParentRowKey(rowWithRef?.row.key, rows);
  const result = targetParentRowKey ? (rowsWithRefs.get(targetParentRowKey) ?? null) : null;
  if (result) {
    return result;
  }
  // in case client pointer is outside of rows, take first or last by default (root one)
  const firstRowRect = first(rowsArray)?.rowRef?.getBoundingClientRect() ?? { y: 0 };
  const lastRowRect = last(rowsArray)?.rowRef?.getBoundingClientRect() ?? { y: 0, height: 0 };
  const rootRowsWithRefs = rowsArray.filter(({ row }) => isEmpty(row.parents));
  if (clientY < firstRowRect.y) {
    return first(rootRowsWithRefs) ?? null;
  } else if (clientY > lastRowRect.y + lastRowRect.height) {
    return last(rootRowsWithRefs) ?? null;
  } else {
    return null;
  }
};

const getParentRowKey = (key: string | undefined, rows: Row[]): string | undefined => {
  const row = rows.find((row) => row.key === key);

  if (!row) {
    return undefined;
  }
  if (row.parents.length === 0) {
    return key;
  } else {
    return rows.find(({ key }) => key === row.parents[0])?.key;
  }
};

type IsDroppableAreaData = {
  rows: Row[];
  draggingRow: RefObject<RowWithRef<Row> | null>;
  sourceRowWithRef: RowWithRef<Row> | undefined;
  targetRowWithRef: RowWithRef<Row> | null;
  calendarMode: CalendarMode;
  calendarRoot: CalendarRoot;
};

type IsDroppableAreaReturn = {
  isDroppable: boolean;
  isReturnedToSourceRow: boolean;
};

export const isDroppableArea = ({
  rows,
  draggingRow,
  targetRowWithRef,
  sourceRowWithRef,
  calendarMode,
  calendarRoot
}: IsDroppableAreaData): IsDroppableAreaReturn => {
  const sourceRow = rows.find(({ key }) => key === sourceRowWithRef?.row.key);
  const targetRow = rows.find(({ key }) => key === targetRowWithRef?.row.key);

  if (!sourceRowWithRef || !targetRowWithRef || !sourceRow || !targetRow) {
    return {
      isDroppable: false,
      isReturnedToSourceRow: false
    };
  }

  const isSourceRow = checkIsSourceRow(
    sourceRow,
    targetRow,
    sourceRowWithRef,
    targetRowWithRef,
    rows,
    calendarMode
  );
  const isReturnedToSourceRow = isSourceRow && draggingRow.current?.row.key !== sourceRowWithRef.row.key;
  const isRightRowType = targetRow.type === RowType.Profile;
  const targetRowProfile = isRightRowType
    ? (getOriginalResource(targetRow, calendarRoot) as WorkforceItemResource | ProfileBasic)
    : null;
  const isTargetProfileSuspended = targetRowProfile && !!targetRowProfile.suspendedAt;

  const isDroppable =
    !!targetRowWithRef.rowRef && !isSourceRow && isRightRowType && !isTargetProfileSuspended;

  return {
    isReturnedToSourceRow,
    isDroppable
  };
};

const checkIsSourceRow = (
  sourceRow: Row,
  targetRow: Row,
  sourceRowWithRef: RowWithRef<Row>,
  targetRowWithRef: RowWithRef<Row>,
  rows: Row[],
  calendarMode: CalendarMode
): boolean => {
  const sourceRowParentKey = sourceRow.parents.length > 0 ? sourceRow.parents[0] : sourceRowWithRef.row.key;

  const sourceSubRowKeys = rows
    .filter(({ parents }) => parents.length > 0 && parents[0] === sourceRowParentKey)
    .map(({ key }) => key);

  if (calendarMode === CalendarMode.Projects) {
    const originalsAreTheSame =
      targetRow.type === RowType.Profile && sourceRow.original.id === targetRow.original.id;

    return targetRowWithRef.row.key === sourceRowWithRef.row.key || originalsAreTheSame;
  } else {
    return (
      targetRowWithRef.row.key === sourceRowParentKey || sourceSubRowKeys.includes(targetRowWithRef.row.key)
    );
  }
};

export const calculateDropZoneHeight = (
  currentTargetRowWithRef: RowWithRef<Row>,
  rows: Row[],
  rowsWithRefs: RowsWithRefs<Row>
) => {
  if (!currentTargetRowWithRef?.rowRef) {
    return 0;
  }

  const targetSubRowKeys = rows
    .filter((row) => row.parents.includes(currentTargetRowWithRef.row.key))
    .map(({ key }) => key);

  const targetSubRowRefs = targetSubRowKeys
    .map((key) => rowsWithRefs.get(key))
    .filter((item): item is RowWithRef<Row> => item !== undefined);

  const childrenRowsHeight = targetSubRowRefs.reduce((acc, { rowRef }) => {
    const rowHeight = rowRef ? acc + rowRef.getBoundingClientRect().height : acc;
    const borderTopWidth = parseFloat(rowRef ? window.getComputedStyle(rowRef).borderTopWidth : "0px");
    return rowHeight - borderTopWidth;
  }, 0);

  const rootRowHeight = currentTargetRowWithRef.rowRef.getBoundingClientRect().height;
  const rootRowBorderTopWidth = parseFloat(
    currentTargetRowWithRef.rowRef
      ? window.getComputedStyle(currentTargetRowWithRef.rowRef).borderTopWidth
      : "0px"
  );

  return childrenRowsHeight + (rootRowHeight - rootRowBorderTopWidth);
};

export const getSourceProfileOriginal = (
  row: Row,
  rows: Row[],
  calendarMode: CalendarMode
): RowOriginal | undefined => {
  if (calendarMode === CalendarMode.Projects) {
    return row.original;
  }
  return row.parents.length > 0 ? rows.find(({ key }) => row.parents[0] === key)?.original : row.original;
};
export const getTargetProfileId = (
  row: Row,
  targetRow: Row | undefined,
  rows: Row[],
  calendarMode: CalendarMode
): Id | undefined => {
  if (calendarMode === CalendarMode.Projects) {
    return targetRow?.original.id;
  }
  return targetRow
    ? targetRow.parents.length > 0
      ? rows.find(({ key }) => row.parents[0] === key)?.original.id
      : targetRow.original.id
    : undefined;
};

type PositionAttributes = "top" | "left" | "width" | "height";

export const updateHTMLDivElementPosition = (
  element: HTMLDivElement | null,
  positionToUpdate: Partial<Record<PositionAttributes, string>>
) => {
  Object.entries(positionToUpdate).forEach(([key, value]) => {
    if (element) {
      element.style[key] = value;
    }
  });
};

export const getSuspendedProfileDetails = (
  data: ProfileBasic
): BookingDetailSidePanelProfile | undefined => ({
  id: data.id,
  firstName: data.firstName,
  lastName: data.lastName ?? undefined,
  suspendedAt: data.suspendedAt,
  status: "inactive"
});

export const getMaxVerticalTop = ({
  rowsWithRefs,
  initialClientPositionY
}: {
  rowsWithRefs: RowsWithRefs<Row> | undefined;
  initialClientPositionY: number;
}): { maxTopUp: number; maxTopDown: number } => {
  const rowsArray = rowsWithRefs ? Array.from(rowsWithRefs.values()) : [];
  const firstRowRect = first(rowsArray)?.rowRef?.getBoundingClientRect() ?? { y: 0 };
  const lastRowRect = last(rowsArray)?.rowRef?.getBoundingClientRect() ?? { y: 0, height: 0 };

  // calculate max top value limited by first and last row
  const maxTopUp = firstRowRect.y - (initialClientPositionY ?? 0);
  const maxTopDown = lastRowRect.y - (initialClientPositionY ?? 0) + (lastRowRect.height ?? 0);

  return { maxTopUp, maxTopDown };
};

type CanDragBooking = {
  isAuditBooking: boolean;
  sourceRowWithRef: RowWithRef<Row> | undefined;
  targetRowRef: RowWithRef<Row> | null;
  draggedDays: number | null;
  canDragVerticallyBaseCheck: boolean;
};

export const canDragBooking = ({
  isAuditBooking,
  sourceRowWithRef,
  targetRowRef,
  draggedDays,
  canDragVerticallyBaseCheck
}: CanDragBooking) => {
  const canDragHorizontallyAuditBooking =
    isAuditBooking && (sourceRowWithRef?.row.key === targetRowRef?.row.key || !targetRowRef);
  const canDragHorizontally = !isAuditBooking || canDragHorizontallyAuditBooking;

  const canDragVerticallyAuditBooking = isAuditBooking && draggedDays === 0;
  const canDragVertically = canDragVerticallyBaseCheck && (!isAuditBooking || canDragVerticallyAuditBooking);

  return {
    canDragHorizontally,
    canDragVertically
  };
};
